import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../helper/base-url";
import {
  DEFAULT_FEE_RULES,
  fetchFeeRules,
  getCachedFeeRules,
} from "../services/platform-settings/fee-rules-service";

const DEFAULT_FLAGS = {
  browseProjects: true,
  browseFreelancers: true,
  messaging: true,
  courses: true,
  reviews: true,
  disputes: true,
  subscriptions: true,
  freelancerServices: true,
};

const PlatformFeatureFlagsContext = createContext({
  flags: DEFAULT_FLAGS,
  fees: DEFAULT_FEE_RULES,
  loading: true,
});

export function PlatformFeatureFlagsProvider({ children }) {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [fees, setFees] = useState(DEFAULT_FEE_RULES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [flagsRes, feeRules] = await Promise.all([
          axios.get(`${BASE_URL}platform-feature-flags`),
          fetchFeeRules({ force: true }),
        ]);
        const data = flagsRes?.data?.result?.data || flagsRes?.data?.data || {};
        if (!cancelled) {
          setFlags({ ...DEFAULT_FLAGS, ...data });
          setFees({ ...DEFAULT_FEE_RULES, ...feeRules });
        }
      } catch {
        if (!cancelled) {
          setFlags(DEFAULT_FLAGS);
          setFees(getCachedFeeRules());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ flags, fees, loading }), [flags, fees, loading]);
  return (
    <PlatformFeatureFlagsContext.Provider value={value}>
      {children}
    </PlatformFeatureFlagsContext.Provider>
  );
}

export function usePlatformFeatureFlags() {
  return useContext(PlatformFeatureFlagsContext);
}

export function usePlatformFeeRules() {
  const ctx = useContext(PlatformFeatureFlagsContext);
  return ctx?.fees || DEFAULT_FEE_RULES;
}
